# Locations

Locations specify the geographical location of a position.
Precisely locating positions helps candidates search for the most relevant jobs.
They are also used as an input into [SEEK’s ad pricing].

Locations are represented as a hierarchy from a larger parent location to smaller child locations.

The hierarchy supports increased granularity in core Asia-Pacific locations where the SEEK Group operates.
It starts with top-level areas and becomes more specific until an individual suburb is identified,
and is designed to be continuously revised to address gaps and realignments over time.

| Code | Name            |
| :--: | :-------------- |
|  AU  | Australia       |
|  HK  | Hong Kong       |
|  ID  | Indonesia       |
|  MY  | Malaysia        |
|  NZ  | New Zealand     |
|  PH  | The Philippines |
|  SG  | Singapore       |
|  TH  | Thailand        |

The hierarchy only extends to the country level (e.g. Fiji) outside of these core Asia-Pacific locations.

For example, this is a subset of the location hierarchy for Victoria, Australia:

```mermaid
graph TD
    AU(Australia) --> VIC(Victoria)
    VIC --> MEL(Melbourne)
    MEL --> CBD(CBD & Inner Suburbs)
    CBD --> Fitzroy
    CBD --> Melbourne

    VIC --> SWC(South West Coast VIC)
    SWC --> Geelong
    SWC --> Torquay
```

[seek’s ad pricing]: /use-cases/job-posting/ad-selection

## Remote work

If a hirer is recruiting for a remote position that is not tied to a specific geographical location, set [`seekWorkArrangementCodes`] to `Remote`.

A location is still required for a remote position;
this can be set to the headquarters or local branch of the hirer’s organization.

[`seekWorkArrangementCodes`]: /schema/named-type/PostPosition_PositionProfileInput#/field/seekWorkArrangementCodes

## Before you begin

- SEEK recommends using a [browser token] to query locations directly from a hirer’s browser.
  For the browser token to work, you will need to include the `query:ontologies` scope in your request.

- You can also query locations from your backend using a [partner token].

[partner token]: /auth/partner-tokens
[browser token]: /auth/browser-tokens

### Contextual names

SEEK’s location queries return a common [Location] object that has both a [`name`] and a [`contextualName`].
When displaying a list of locations to a hirer, you should use the [`contextualName`] as it provides more precise information.
For example, when displaying the results of the [`locationSuggestions` query] to a user, some results may be ambiguous if only the `name` value is displayed.

| name     | contextualName       |
| -------- | -------------------- |
| Richmond | Richmond NSW 2753 AU |
| Richmond | Richmond VIC 3121 AU |
| Richmond | Richmond Otago NZ    |

[Location]: /schema/named-type/Location
[`name`]: /schema/named-type/Location#/field/name
[`contextualName`]: /schema/named-type/Location#/field/contextualName

## Implementation options

Locations can be implemented in one of three ways.
These are listed in order of preference.

### Option 1: Build a SEEK-specific location input

You can include a SEEK-specific auto-suggest field in your job posting flow.
This affords the most control to your hirers, as they get to explicitly select the SEEK location for their job ad.

import { SeekLocationSuggest } from './locations';

<SeekLocationSuggest />

Relevant SEEK locations can be retrieved from the [`locationSuggestions`](#locationsuggestions) query as the hirer types.
The hirer’s identifier should be supplied to the query to tailor suggestions to their SEEK-configured domicile.

The `Detect` button is an optional feature that allows the hirer to use their current location.
You can obtain a latitude and longitude from the geolocation API of their device or browser,
then pass that into the [`nearestLocations`](#nearestlocations) query.
The first returned result represents the closest SEEK location to the provided geolocation by distance.

### Option 2: Map from your internal location hierarchy

If your software already captures the position location based on an internal hierarchy,
you may wish to map this existing value to a SEEK location without additional input from the hirer.
This requires you to have the latitude and longitude associated with each location in your internal hierarchy.

In your job posting flow, prompt the user to select a location from your internal hierarchy as per usual.
Your software should then retrieve the associated geolocation and pass it into the [`nearestLocations`](#nearestlocations) query as described in Option 1.

We recommend running this query at time of job posting to ensure that you retrieve the latest SEEK location match.
You may use this method to build a mapping from your internal locations to their corresponding SEEK location IDs upfront,
but the mapping should be periodically refreshed to handle changes to the SEEK location hierarchy.

### Option 3: Use location inference

If you don’t have the latitude and longitude associated with each location in your internal hierarchy,
you may use other structured information to infer a SEEK location without additional input from the hirer.

import { Alert, Text } from 'braid-design-system';

<Alert tone="caution">
  <Text>
    Location inference is less reliable than the other location options and
    should be used only as a last resort. You should test a significant sample
    of common locations across multiple countries used by hirers to ensure the
    returned locations are accurate for your use case.
  </Text>
</Alert>

In your job posting flow, prompt the user to select a location from your internal hierarchy as per usual.
Your software should then retrieve structured address details of the location and pass it into the [`inferLocation`](#inferlocation) query.

We recommend running this query at time of job posting to ensure that you retrieve the latest SEEK location match.
You may use this method to build a mapping from your internal locations to their corresponding SEEK location IDs upfront,
but the mapping should be periodically refreshed to handle changes to the SEEK location hierarchy and improvements to our inference capability.

## Operations

The SEEK API provides four queries for looking up locations:

1. [`locationSuggestions`](#locationsuggestions) returns locations matching partial text input for an interactive autocomplete user experience.

2. [`nearestLocations`](#nearestlocations) returns locations relevant to the provided geolocation, ordered by distance.

3. [`inferLocation`](#inferlocation) returns a location matching structured address details for programmatic mapping.

4. [`location`](#location) returns a location for a given SEEK location ID.

### locationSuggestions

The [`locationSuggestions` query] returns an array of likely locations for partial text input.
You can limit the number of results using the `first` argument.

This currently expects a substring of a location’s name (e.g. a suburb or city name).
Street addresses are not supported, and postcodes are only supported in Australia at this time.

You must use the `PositionPosting` usage type when the suggestions will be used for [posting a job ad].
This filters out locations that are too general to be associated with a job ad.

```graphql
query (
  $first: Int!
  $hirerId: String!
  $schemeId: String!
  $text: String!
  $usageTypeCode: String!
) {
  locationSuggestions(
    first: $first
    hirerId: $hirerId
    schemeId: $schemeId
    text: $text
    usageTypeCode: $usageTypeCode
  ) {
    location {
      id {
        value
      }
      contextualName
      countryCode

      # Enable your software to pre-select a salary currency
      currencies {
        code
      }
    }
  }
}
```

```json
{
  "first": 2,
  "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h",
  "schemeId": "seekAnzPublicTest",
  "text": "Brisb",
  "usageTypeCode": "PositionPosting"
}
```

```json
{
  "locationSuggestions": [
    {
      "location": {
        "id": {
          "value": "seekAnzPublicTest:location:seek:2dkY4vZJF"
        },
        "contextualName": "Brisbane QLD 4000 AU",
        "countryCode": "AU",
        "currencies": [
          {
            "code": "AUD"
          }
        ]
      }
    },
    {
      "location": {
        "id": {
          "value": "seekAnzPublicTest:location:seek:P7ZBgy51"
        },
        "contextualName": "Brisbane Airport QLD 4008 AU",
        "countryCode": "AU",
        "currencies": [
          {
            "code": "AUD"
          }
        ]
      }
    }
  ]
}
```

[`locationsuggestions` query]: /schema/query/locationSuggestions
[posting a job ad]: /use-cases/job-posting/managing-job-ads/posting-a-job-ad

### nearestLocations

The [`nearestLocations` query] returns an array of locations closest to the specified latitude & longitude, ordered by distance.
You can limit the number of results using the `first` argument.

This can be used to:

- Suggest the location nearest to an end user based on their geolocation from a built-in [platform](https://developer.android.com/reference/android/location/Location/) or [web](https://developer.mozilla.org/en-US/docs/Web/API/Geolocation_API) API.
- Map from your own internal location hierarchy to a SEEK location.

```graphql
query ($first: Int!, $geoLocation: GeoLocationInput!, $schemeId: String!) {
  nearestLocations(
    first: $first
    geoLocation: $geoLocation
    schemeId: $schemeId
  ) {
    id {
      value
    }
    contextualName
    countryCode

    # Enable your software to pre-select a salary currency
    currencies {
      code
    }
  }
}
```

```json
{
  "first": 2,
  "geoLocation": {
    "latitude": -37.840935,
    "longitude": 144.946457
  },
  "schemeId": "seekAnzPublicTest"
}
```

```json
{
  "nearestLocations": [
    {
      "id": {
        "value": "seekAnzPublicTest:location:seek:2YE87KE4o"
      },
      "contextualName": "Port Melbourne VIC 3207 AU",
      "countryCode": "AU",
      "currencies": [
        {
          "code": "AUD"
        }
      ]
    },
    {
      "id": {
        "value": "seekAnzPublicTest:location:seek:muG1pxej"
      },
      "contextualName": "Albert Park VIC 3206 AU",
      "countryCode": "AU",
      "currencies": [
        {
          "code": "AUD"
        }
      ]
    }
  ]
}
```

[`nearestlocations` query]: /schema/query/nearestLocations

### inferLocation

The [`inferLocation` query] returns a location matching structured address details for programmatic mapping.
This query will attempt to match the details to a location in our hierarchy on a best-effort basis.

```graphql
query ($address: SeekPositionAddressInput!, $schemeId: String!) {
  inferLocation(address: $address, schemeId: $schemeId) {
    id {
      value
    }

    # Enable your software to pre-select a salary currency
    currencies {
      code
    }
  }
}
```

```json
{
  "address": {
    "formattedAddress": "60 Cremorne St, Cremorne VIC",
    "postalCode": "3121",
    "countryCode": "AU"
  },
  "schemeId": "seekAnzPublicTest"
}
```

```json
{
  "inferLocation": {
    "id": {
      "value": "seekAnzPublicTest:location:seek:46qaJ6Sj"
    },
    "currencies": [
      {
        "code": "AUD"
      }
    ]
  }
}
```

If your location hierarchy lacks separate `postalCode` data,
you can provide the address as a single formatted line along with a country code,
though this may produce less consistent results for some locations:

```graphql
query ($address: SeekPositionAddressInput!, $schemeId: String!) {
  inferLocation(address: $address, schemeId: $schemeId) {
    id {
      value
    }

    # Enable your software to pre-select a salary currency
    currencies {
      code
    }
  }
}
```

```json
{
  "address": {
    "formattedAddress": "60 Cremorne St, Cremorne VIC 3121",
    "countryCode": "AU"
  },
  "schemeId": "seekAnzPublicTest"
}
```

```json
{
  "inferLocation": {
    "id": {
      "value": "seekAnzPublicTest:location:seek:46qaJ6Sj"
    },
    "currencies": [
      {
        "code": "AUD"
      }
    ]
  }
}
```

#### inferLocation input formats

SEEK will infer a location based on the closest match to the supplied text.
You should test and fine tune the data format provided by your software,
starting with the following recommended baselines:

| Country     | Fields               | Example `formattedAddress` | Example `countryCode` | Example `postCode` | Response `contextualName`     |
| :---------- | :------------------- | :------------------------- | :-------------------- | :----------------- | :---------------------------- |
| Australia   | suburb               | Cremorne                   | AU                    | 3121               | Cremorne VIC 3121 AU          |
| Australia   | suburb, state        | Richmond, VIC              | AU                    | 3121               | Richmond VIC 3121 AU          |
| New Zealand | suburb, [region]     | Richmond, Tasman           | NZ                    |                    | Richmond, Tasman NZ           |
| Philippines | city                 | Makati                     | PH                    |                    | Makati City Metro Manila PH   |
| Singapore   | city                 | Tiong Bahru                | SG                    | 150167             | Tiong Bahru Central Region SG |
| Malaysia    | city, province/state | George Town, Penang        | MY                    |                    | George Town Penang MY         |
| Indonesia   | city, province/state | Kembangan, Jakarta         | ID                    |                    | Kembangan Jakarta ID          |
| Hong Kong   | city, province/state | Tai Koo, Eastern District  | HK                    |                    | Tai Koo Eastern District HK   |

The [`inferLocation` query] will only return a single location suitable for [posting a job ad],
similar to the `PositionPosting` usage type for [`locationSuggestions`](#locationsuggestions),
and will return `null` if a valid location cannot be inferred from the provided data.

Issues typically occur when the location data supplied is too broad or ambiguous, such as the examples below:

| formattedAddress | countryCode | postCode | Response                                                                                                                                              |
| ---------------- | ----------- | -------- | ----------------------------------------------------------------------------------------------------------------------------------------------------- |
| Malaysia         | MY          |          | <p>`null`</p><p>Location is too broad</p>                                                                                                             |
| New South Wales  | AU          |          | <p>`null`</p><p>Location is too broad</p>                                                                                                             |
| Richmond         | AU          |          | <p>Richmond QLD 4822 AU</p><p>Potential for mismatch, for example if user intended Richmond VIC. Including postcode will aid accuracy of results.</p> |

If null is returned, the hirer will be unable to proceed to post their job ad to SEEK and you should:

1. Surface meaningful and actionable error messaging to users within the job posting flow, providing an opportunity to amend the location before retrying.
   For example: `Unable to retrieve location for SEEK. Update your location and ensure you are entering a granular city or suburb.`
2. Review the quality of the address details that your software sends to the SEEK API

[`inferlocation` query]: /schema/query/inferLocation
[region]: https://en.wikipedia.org/wiki/Regions_of_New_Zealand

### location

The [`location` query] returns the location for a given location ID.
This can be useful for debugging or exploratory testing:

```graphql
query ($id: String!) {
  location(id: $id) {
    name
    contextualName
    countryCode

    # Enable your software to pre-select a salary currency
    currencies {
      code
    }
  }
}
```

```json
{
  "id": "seekAnzPublicTest:location:seek:2dkY4vZJF"
}
```

```json
{
  "location": {
    "name": "Brisbane",
    "contextualName": "Brisbane QLD 4000 AU",
    "countryCode": "AU",
    "currencies": [
      {
        "code": "AUD"
      }
    ]
  }
}
```

[`location` query]: /schema/query/location
